// src/admin/pages/customers/CustomersManagement.tsx
// Customers who registered an account on the client website — list + deactivate.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Refresh as RefreshIcon, Search as SearchIcon, Group as GroupIcon } from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { CustomerService, Customer, CustomerStats } from './customerService';
import CustomerTable from './CustomerTable';
import ToastNotification from '../common/Toastnotification';

const PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const CustomersManagement: React.FC = () => {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canEdit = isSuperAdmin || hasPermission('customers.edit');

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stats, setStats] = useState<CustomerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [toggleLoading, setToggleLoading] = useState('');
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const showToast = (message: string, severity: ToastState['severity']) => setToast({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const statusValue = statusFilter === 'all' ? undefined : statusFilter === 'active';
      const [result, statsResult] = await Promise.all([
        CustomerService.getCustomersPaginated(PER_PAGE, currentPage, searchQuery, statusValue),
        CustomerService.getCustomerStats(),
      ]);
      setCustomers(result.customers);
      setTotalCount(result.totalCount);
      setStats(statsResult);
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      setCurrentPage(1);
    }
  };

  const handleToggleStatus = async (customer: Customer) => {
    setToggleLoading(customer.uid);
    try {
      await CustomerService.toggleCustomerStatus(customer.uid, !customer.isActive);
      showToast(
        customer.isActive ? `${customer.fullName || customer.email} deactivated` : `${customer.fullName || customer.email} activated`,
        'success'
      );
      await load();
    } catch (err: any) {
      showToast(err.message || 'Failed to update customer status', 'error');
    } finally {
      setToggleLoading('');
    }
  };

  const totalPages = Math.ceil(totalCount / PER_PAGE);

  if (loading && customers.length === 0 && !errorMsg) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px" flexDirection="column">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>Loading customers...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: { md: '100%' }, display: 'flex', flexDirection: 'column', overflow: { xs: 'visible', md: 'hidden' }, p: { xs: 2, sm: 3 } }}>
      <Box sx={{ flexShrink: 0 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <GroupIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Customers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Everyone who registered an account on the website
              </Typography>
            </Box>
          </Stack>
          <Button onClick={load} variant="outlined" startIcon={<RefreshIcon />} disabled={loading}>Refresh</Button>
        </Stack>

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
            {errorMsg}
          </Alert>
        )}

        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }}>
          <Grid item xs={4}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Total Customers</Typography>
                <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' } }}>{stats?.totalCustomers ?? '—'}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Active</Typography>
                <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, color: 'success.main' }}>{stats?.activeCustomers ?? '—'}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Deactivated</Typography>
                <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, color: 'error.main' }}>{stats?.deactivatedCustomers ?? '—'}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              placeholder="Search by name, email, or phone... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              fullWidth
              size="small"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            <TextField
              select
              size="small"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as any); setCurrentPage(1); }}
              sx={{ minWidth: { xs: '100%', sm: 180 } }}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Deactivated</MenuItem>
            </TextField>
          </Stack>
        </Paper>
      </Box>

      <Box sx={{ flex: 1, overflow: { xs: 'visible', md: 'auto' }, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
        {customers.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {searchQuery || statusFilter !== 'all' ? 'No Customers Match Your Filters' : 'No Customers Yet'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {searchQuery || statusFilter !== 'all' ? 'Try a different search term or filter.' : 'Customers will appear here once they sign up on the website.'}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {customers.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
            <Box sx={{ flex: 1, overflow: 'auto', mb: 2, minHeight: { xs: 'auto', md: 0 } }}>
              <CustomerTable customers={customers} onToggleStatus={handleToggleStatus} toggleLoading={toggleLoading} isDark={isDark} canEdit={canEdit} />
            </Box>
            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 3 }} alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalCount === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1}–{Math.min(currentPage * PER_PAGE, totalCount)} of {totalCount} customers
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={(_e, page) => setCurrentPage(page)}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      <ToastNotification open={toast.open} message={toast.message} severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))} />
    </Box>
  );
};

export default CustomersManagement;
